import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { HelperService } from '../../services/helper-service';
import { ManageUserService } from '../../services/manage-user-service';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-add-new-user',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RouterLinkActive],
  templateUrl: './add-new-user.component.html',
  styleUrl: './add-new-user.component.css',
})
export class AddNewUserComponent implements OnInit {
  defaultProfileImage = '/assets/img/defaultUser.png';
  profileImagePreview: string = '';
  profileImageFile: File | null = null;

  userRoles: string[] = ['Administrator', 'Member Advocate', 'Nutritionist', 'Doctor', 'Fitness Instructor', 'Psychologists'];
  genders: string[] = ['Male', 'Female', 'Others'];
  countryOptions = [
    { value: 'india', label: 'India' },
    { value: 'United States', label: 'Usa' },
  ];

  states: any[] = [];
  cities: any[] = [];

  user = {
    firstName: '',
    lastName: '',
    mobileNo: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    country: 'india',
    state: '',
    city: '',
    zipCode: '',
    userRole: '',
    gender: '',
    facebookUrl: '',
    twitterUrl: '',
    instagramUrl: '',
    linkdinUrl: '',
  };

  submitting = false;

  // Set when opened from the Update button in the user list (/user/update-user/:userId)
  editUserId: string | null = null;
  loadingUser = false;
  // Values as loaded from the API, used to send only the edited fields on update
  private originalUser: typeof this.user | null = null;

  // Form field -> multipart key sent to the backend
  private readonly updateFieldKeys: Record<keyof typeof this.user, string> = {
    firstName: 'firstName',
    lastName: 'lastName',
    mobileNo: 'mobileNo',
    email: 'email',
    addressLine1: 'addressLine1',
    addressLine2: 'addressLine2',
    country: 'country',
    state: 'state',
    city: 'city',
    zipCode: 'zipCode',
    userRole: 'role',
    gender: 'gender',
    facebookUrl: 'facebookUrl',
    twitterUrl: 'twitterUrl',
    instagramUrl: 'instagramUrl',
    linkdinUrl: 'linkdinUrl',
  };

  get isEditMode(): boolean {
    return !!this.editUserId;
  }

  constructor(
    private helperService: HelperService,
    private manageUserService: ManageUserService,
    private toast: ToastService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.editUserId = this.route.snapshot.paramMap.get('userId');
    if (this.isEditMode) {
      this.loadUserDetails(this.editUserId!);
    } else {
      this.loadCountryStateData(this.user.country);
    }
  }

  private loadCountryStateData(country: string, onLoaded?: () => void): void {
    this.helperService.getCountryStateForAddForm(country).subscribe((response: any) => {
      this.cities = response[1];
      this.states = response[2];
      onLoaded?.();
    });
  }

  private loadUserDetails(userId: string): void {
    this.loadingUser = true;
    this.manageUserService.getUserDetailsById(userId).subscribe({
      next: (response: any) => {
        this.loadingUser = false;
        const data = response?.result ?? response;
        if (!data) {
          this.toast.errorMessage('User details not found.');
          return;
        }
        this.populateForm(data);
      },
      error: (err) => {
        this.loadingUser = false;
        this.toast.errorMessage(this.extractErrorMessage(err));
      },
    });
  }

  private populateForm(data: any): void {
    this.user = {
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      mobileNo: data.mobileNo ?? '',
      email: data.email ?? '',
      addressLine1: data.addressLine1 ?? '',
      addressLine2: data.addressLine2 ?? '',
      country: this.matchCountryOption(data.country),
      state: data.state ?? '',
      city: data.city ?? '',
      zipCode: data.zipCode ?? '',
      userRole: data.userRoles?.[0]?.userRole ?? data.role ?? '',
      gender: data.gender ?? '',
      facebookUrl: data.facebookUrl ?? '',
      twitterUrl: data.twitterUrl ?? '',
      instagramUrl: data.instagramUrl ?? '',
      linkdinUrl: data.linkdinUrl ?? '',
    };
    this.profileImagePreview = data.profilePictureUrl ?? '';
    this.profileImageFile = null;
    // Baseline keeps the raw API values, so a dropdown value corrected below is sent on update
    this.originalUser = { ...this.user };

    // A saved value that isn't one of the dropdown options would show as blank but still pass validation,
    // so snap each dropdown to its matching option or clear it
    this.user.userRole = this.matchOption(this.user.userRole, this.userRoles);
    this.user.gender = this.matchOption(this.user.gender, this.genders);

    // Load states for the user's country, then the cities for their state, so the saved values show in the dropdowns
    this.loadCountryStateData(this.user.country, () => {
      this.user.state = this.matchOption(this.user.state, (this.states ?? []).map((state: any) => state.name));
      const stateMatch = this.states?.find((state: any) => state.name === this.user.state);
      this.cities = stateMatch?.cities ?? [];
      this.user.city = this.matchOption(this.user.city, this.cities.map((city: any) => city.name));
    });
  }

  private matchOption(value: string, options: string[]): string {
    const normalized = (value ?? '').toString().trim().toLowerCase();
    if (!normalized) {
      return '';
    }
    return options.find((option) => (option ?? '').toLowerCase() === normalized) ?? '';
  }

  private matchCountryOption(country: string): string {
    const match = this.countryOptions.find(
      (option) =>
        option.value.toLowerCase() === (country ?? '').toLowerCase() ||
        option.label.toLowerCase() === (country ?? '').toLowerCase()
    );
    return match?.value ?? 'india';
  }

  onCountryChange(event: any): void {
    const country = event.target.value;
    this.user.country = country;
    this.user.state = '';
    this.user.city = '';
    this.loadCountryStateData(country);
  }

  onStateChange(event: any): void {
    const selectedState = event.target.value;
    this.user.city = '';
    const stateMatch = this.states?.filter((state: any) => state.name === selectedState);
    this.cities = stateMatch?.[0]?.cities || [];
  }

  onProfileImageSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      this.toast.errorMessage('Only .jpg .png .jpeg allowed');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.profileImageFile = file;
      this.profileImagePreview = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  private isFormValid(): boolean {
    const requiredFields: [keyof typeof this.user, string][] = [
      ['firstName', 'Please enter first name.'],
      ['lastName', 'Please enter last name.'],
      ['mobileNo', 'Please enter mobile number.'],
      ['email', 'Please enter email.'],
      ['userRole', 'Please select user role.'],
      ['gender', 'Please select user gender.'],
      ['state', 'Please select state.'],
      ['city', 'Please select town/city.'],
      ['zipCode', 'Please enter pin code.'],
    ];
    // Whitespace-only values count as empty
    const missing = requiredFields.find(([field]) => !(this.user[field] ?? '').toString().trim());
    if (missing) {
      this.toast.errorMessage(missing[1]);
      return false;
    }
    return true;
  }

  onSubmit(): void {
    if (this.isEditMode) {
      this.updateUserDetails();
    } else {
      this.sendInvite();
    }
  }

  private updateUserDetails(): void {
    if (!this.editUserId || !this.originalUser) {
      this.toast.errorMessage('User details are not loaded yet.');
      return;
    }
    if (!this.isFormValid()) {
      return;
    }

    const formData = new FormData();
    let hasChanges = false;
    (Object.keys(this.updateFieldKeys) as (keyof typeof this.user)[]).forEach((field) => {
      const current = (this.user[field] ?? '').toString().trim();
      const original = (this.originalUser![field] ?? '').toString().trim();
      if (current !== original) {
        formData.append(this.updateFieldKeys[field], current);
        hasChanges = true;
      }
    });
    if (this.profileImageFile) {
      formData.append('pictureType', this.profileImageFile.type);
      formData.append('picture', this.profileImageFile, this.profileImageFile.name);
      hasChanges = true;
    }

    if (!hasChanges) {
      this.toast.infoMessage('No changes to update.');
      return;
    }

    this.submitting = true;
    this.manageUserService.updateUserDetails(this.editUserId, formData).subscribe({
      next: () => {
        this.submitting = false;
        this.toast.successMessage('User details updated successfully');
        this.router.navigate(['/user']);
      },
      error: (err) => {
        this.submitting = false;
        this.toast.errorMessage(this.extractErrorMessage(err));
      },
    });
  }

  sendInvite(): void {
    if (!this.isFormValid()) {
      return;
    }

    const formData = new FormData();
    formData.append('email', this.user.email);
    formData.append('firstName', this.user.firstName);
    formData.append('lastName', this.user.lastName);
    formData.append('password', '');
    formData.append('mobileNo', this.user.mobileNo);
    formData.append('role', this.user.userRole);
    formData.append('country', this.user.country);
    formData.append('state', this.user.state);
    formData.append('city', this.user.city);
    formData.append('zipCode', this.user.zipCode);
    formData.append('addressLine1', this.user.addressLine1);
    formData.append('addressLine2', this.user.addressLine2);
    formData.append('timeZone', 'UTC');
    formData.append('gender', this.user.gender);
    formData.append('facebookUrl', this.user.facebookUrl);
    formData.append('instagramUrl', this.user.instagramUrl);
    formData.append('linkdinUrl', this.user.linkdinUrl);
    formData.append('twitterUrl', this.user.twitterUrl);
    formData.append('profilePictureUrl', '');
    if (this.profileImageFile) {
      formData.append('pictureType', this.profileImageFile.type);
      formData.append('picture', this.profileImageFile, this.profileImageFile.name);
    }

    this.submitting = true;
    this.manageUserService.inviteUser(formData).subscribe({
      next: () => {
        this.submitting = false;
        this.toast.successMessage('Invite sent successfully');
        this.resetForm();
      },
      error: (err) => {
        this.submitting = false;
        this.toast.errorMessage(this.extractErrorMessage(err));
      },
    });
  }

  private extractErrorMessage(err: any): string {
    const body = err?.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    // Validation errors, e.g. 409 { errors: [{ name: 'email', reason: 'The email ... is already present.' }] }
    if (Array.isArray(body?.errors)) {
      const reasons = body.errors.map((e: any) => e?.reason).filter((r: any) => !!r);
      if (reasons.length) {
        return reasons.join(' ');
      }
    }
    if (body?.message) {
      return body.message;
    }
    return 'Something went wrong. Please try again.';
  }

  private resetForm(): void {
    this.user = {
      firstName: '',
      lastName: '',
      mobileNo: '',
      email: '',
      addressLine1: '',
      addressLine2: '',
      country: 'india',
      state: '',
      city: '',
      zipCode: '',
      userRole: '',
      gender: '',
      facebookUrl: '',
      twitterUrl: '',
      instagramUrl: '',
      linkdinUrl: '',
    };
    this.profileImageFile = null;
    this.profileImagePreview = '';
    this.loadCountryStateData(this.user.country);
  }
}
